import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, X } from "lucide-react";
import { Config, DealAccessApi, Deals } from "@/api/endpoints";
import { apiErrorMessage } from "@/api/client";
import { useAuthStore } from "@/store/auth";
import OwnerForm, { type OwnerRole } from "@/components/OwnerForm";
import { toast } from "@/lib/feedback";

interface Props {
  dealId: string;
}

function OwnerRow({
  name,
  canRemove,
  onRemove,
}: {
  name: string;
  canRemove: boolean;
  onRemove: () => void;
}) {
  return (
    <li className="panel-tight p-2.5 flex items-center justify-between gap-2">
      <span className="text-md font-medium truncate">{name}</span>
      {canRemove && (
        <button
          type="button"
          className="btn btn-ghost shrink-0"
          onClick={onRemove}
          aria-label={`Remove ${name}`}
          title="Remove"
        >
          <X className="w-4 h-4" />
        </button>
      )}
    </li>
  );
}

/** Owners card. Ownership is stored in deal_access (single source of truth): the
 *  primary owner is the `is_primary` owner grant, secondaries are the other owner
 *  grants. Editing goes through the admin-only grant/revoke endpoints, so the
 *  card is read-only for non-admins (matching the "Assign deals" admin panel). */
export default function OwnersSection({ dealId }: Props) {
  const qc = useQueryClient();
  const me = useAuthStore((s) => s.user);
  const [adding, setAdding] = useState(false);

  const dealQuery = useQuery({
    queryKey: ["deal", dealId],
    queryFn: () => Deals.get(dealId),
    enabled: !!dealId,
  });
  const usersQuery = useQuery({ queryKey: ["owners"], queryFn: () => Config.listOwners() });
  const accessQuery = useQuery({
    queryKey: ["deal-access", dealId],
    queryFn: () => DealAccessApi.list(dealId),
    enabled: !!dealId,
  });

  // Admins manage everything; a non-admin primary owner may manage SECONDARY
  // owners on their own deal (the backend enforces this). Reassigning/removing
  // the primary stays admin-only so a rep can't orphan their own deal.
  const isAdmin = me?.role === "admin";
  const iAmPrimary = (accessQuery.data ?? []).some((g) => g.user_id === me?.id && g.is_primary);
  const canManageSecondary = isAdmin || iAmPrimary;
  const canManagePrimary = isAdmin;

  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ["deal", dealId] });
    void qc.invalidateQueries({ queryKey: ["deals"] });
    void qc.invalidateQueries({ queryKey: ["dashboard"] });
    void qc.invalidateQueries({ queryKey: ["deal-access", dealId] });
    void qc.invalidateQueries({ queryKey: ["user-deal-access"] });
  };

  const grantMutation = useMutation({
    mutationFn: (v: { userId: string; isPrimary: boolean }) =>
      DealAccessApi.grant(dealId, {
        user_id: v.userId,
        capacity: "owner",
        is_primary: v.isPrimary,
      }),
    onSuccess: () => {
      invalidate();
      setAdding(false);
    },
    onError: (e) => toast.err(`Could not assign owner: ${apiErrorMessage(e)}`),
  });

  const revokeMutation = useMutation({
    mutationFn: (userId: string) => DealAccessApi.revoke(dealId, userId),
    onSuccess: invalidate,
    onError: (e) => toast.err(`Could not remove owner: ${apiErrorMessage(e)}`),
  });

  const deal = dealQuery.data;
  const primaryOwner = deal?.primary_owner ?? null;
  const secondaryOwners = deal?.secondary_owners ?? [];
  const users = usersQuery.data ?? [];
  const userIdByName = (name: string) => users.find((u) => u.name === name)?.id;

  const handleAdd = (role: OwnerRole, name: string) => {
    const userId = userIdByName(name);
    if (!userId) return;
    const isPrimary = role === "primary";
    if (isPrimary ? canManagePrimary : canManageSecondary) {
      grantMutation.mutate({ userId, isPrimary });
    }
  };
  const removeOwner = (name: string, isPrimary: boolean) => {
    const userId = userIdByName(name);
    if (!userId) return;
    if (isPrimary ? canManagePrimary : canManageSecondary) revokeMutation.mutate(userId);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-md font-semibold">Owners</h2>
        {canManageSecondary && !adding && (
          <button type="button" className="btn btn-ghost" onClick={() => setAdding(true)}>
            <Plus className="w-3.5 h-3.5" /> Add owner
          </button>
        )}
      </div>

      {adding && (
        <OwnerForm
          users={users}
          primaryOwner={primaryOwner}
          secondaryOwners={secondaryOwners}
          onSubmit={handleAdd}
          onCancel={() => setAdding(false)}
          submitting={grantMutation.isPending}
          allowPrimaryRole={isAdmin}
        />
      )}

      {dealQuery.isLoading ? (
        <div className="text-md text-mut-300">Loading…</div>
      ) : (
        <div className="space-y-4">
          <div>
            <div className="label">Primary owner</div>
            {primaryOwner ? (
              <ul className="space-y-1.5">
                <OwnerRow
                  name={primaryOwner}
                  canRemove={canManagePrimary}
                  onRemove={() => removeOwner(primaryOwner, true)}
                />
              </ul>
            ) : (
              <div className="text-sm text-mut-500 italic">None yet</div>
            )}
          </div>

          <div>
            <div className="label">Secondary owners</div>
            {secondaryOwners.length === 0 ? (
              <div className="text-sm text-mut-500 italic">None yet</div>
            ) : (
              <ul className="space-y-1.5">
                {secondaryOwners.map((name) => (
                  <OwnerRow
                    key={name}
                    name={name}
                    canRemove={canManageSecondary}
                    onRemove={() => removeOwner(name, false)}
                  />
                ))}
              </ul>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
